import { c as _c } from "react/compiler-runtime";
import { DropdownMenuItem, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger } from "../../dropdown";
import Tooltip from "../../tooltip";
import { useActionsContext } from "../table-filters/context";
import React, { useCallback, useMemo, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { cx } from "@/lib/tailwind-utils";
import { FilterEditContext, type FilterEditContextValue } from "./edit-context";
import { singleBooleanValue } from "./filter-types/boolean";
import { BOOLEAN_FILTER_OPTIONS } from "./filter-types/boolean-options";
import { formatFilterDisplayValue } from "./helpers";
import { useFilterFocus } from "./hooks";
import { FilterInput, filterContentWidthClass } from "./render-input";
import type { FilterTypeProps } from "./types";
const isEmptyValue = (value: any) => value === undefined || value === null || value === "" || Array.isArray(value) && value.length === 0;

/**
 * A boolean filter offering exactly Yes and No (no relabelled or "Not set"
 * choices), which the list can show as a switch.
 */
const isPlainYesNo = (type: string, options: FilterTypeProps["options"]) => {
  if (type !== "boolean") return false;
  const choices = options?.length ? options : BOOLEAN_FILTER_OPTIONS;
  return choices.length === BOOLEAN_FILTER_OPTIONS.length && BOOLEAN_FILTER_OPTIONS.every(plain => choices.some(choice => String(choice.value) === plain.value && choice.label === plain.label));
};

/**
 * The switch drawn inside a filter row. The row itself is the control, so
 * this is presentational. "unset" is a greyed track with the thumb centred:
 * neither on nor off, the filter isn't applied.
 */
const FilterSwitch = t0 => {
  const $ = _c(12);
  const {
    state
  } = t0;
  const t1 = state === "on" ? "bg-primary" : "bg-muted";
  const t2 = state === "unset" && "opacity-40";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cx("pointer-events-none relative ml-3 inline-flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 shadow-inner ring-1 ring-inset ring-border/5 transition-all", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = state === "on" && "translate-x-2";
  const t5 = state === "off" && "translate-x-0";
  const t6 = state === "unset" && "translate-x-1";
  let t7;
  if ($[3] !== t4 || $[4] !== t5 || $[5] !== t6) {
    t7 = cx("h-3 w-4 rounded-full bg-background/80 shadow-lg backdrop-blur-sm transition-transform duration-150 ease-in-out", t4, t5, t6);
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t7) {
    t8 = <span className={t7} />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t3 || $[10] !== t8) {
    t9 = <span aria-hidden="true" className={t3}>{t8}</span>;
    $[9] = t3;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
};

/**
 * DropdownTableFilter — renders a filter as a sub-menu item inside the main
 * Filters dropdown panel.
 *
 * Write semantics: every edit writes through to `filterSetter` immediately.
 * The parent (TableFiltersProvider) buffers those writes and flushes on
 * main-panel close. We can't buffer locally here because Radix unmounts
 * sub-menus on outside-click without firing onOpenChange(false), which
 * would lose buffered edits.
 */
const DropdownTableFilter: React.FC<FilterTypeProps> = t0 => {
  const $ = _c(111);
  const {
    label,
    type,
    filterValue,
    filterSetter,
    options,
    disableClear: t1,
    icon: Icon,
    onBack,
    direction,
    openEnded
  } = t0;
  const disableClear = t1 === undefined ? false : t1;
  const {
    isMobile
  } = useIsMobile();
  const clock = useClinicClock();
  const {
    focusRef,
    inputRef
  } = useFilterFocus();
  const [isOpen, setIsOpen] = useState(false);
  const {
    setOpen: setPanelOpen
  } = useActionsContext();
  let t2;
  if ($[0] !== filterSetter) {
    t2 = e => {
      e.stopPropagation();
      filterSetter(undefined);
    };
    $[0] = filterSetter;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClearFilter = t2;
  let t3;
  if ($[2] !== filterValue) {
    t3 = {
      value: filterValue
    };
    $[2] = filterValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== setPanelOpen) {
    t4 = () => setPanelOpen(false);
    $[4] = setPanelOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== filterSetter || $[7] !== t4) {
    t5 = {
      onChange: filterSetter,
      submit: t4
    };
    $[6] = filterSetter;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== direction || $[10] !== focusRef || $[11] !== label || $[12] !== onBack || $[13] !== openEnded || $[14] !== options || $[15] !== type) {
    t6 = {
      type,
      label,
      options,
      onBack,
      focusRef,
      direction,
      openEnded
    };
    $[9] = direction;
    $[10] = focusRef;
    $[11] = label;
    $[12] = onBack;
    $[13] = openEnded;
    $[14] = options;
    $[15] = type;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t3 || $[18] !== t5 || $[19] !== t6) {
    t7 = {
      state: t3,
      actions: t5,
      meta: t6
    };
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const editContextValue = t7;
  let t8;
  if ($[21] !== inputRef) {
    t8 = e_0 => {
      if (e_0.target !== e_0.currentTarget) {
        return;
      }
      const el = e_0.currentTarget;
      if (el instanceof HTMLElement && el.dataset.state === "open" && inputRef.current) {
        inputRef.current.focus();
      }
    };
    $[21] = inputRef;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleContentAnimationEnd = t8;
  const [openSide, setOpenSide] = useState("right");
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = node => {
      if (!node) {
        return;
      }
      const sync = () => {
        const side = node.dataset.side;
        if (side === "left" || side === "right") {
          setOpenSide(side);
        }
      };
      sync();
      const observer = new MutationObserver(sync);
      observer.observe(node, {
        attributes: true,
        attributeFilter: ["data-side"]
      });
      return () => observer.disconnect();
    };
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const trackOpenSide = t9;
  if (type === "toggle") {
    const checked = filterValue === true;
    let t10;
    if ($[24] !== checked || $[25] !== filterSetter) {
      t10 = event => {
        event.preventDefault();
        filterSetter(checked ? undefined : true);
      };
      $[24] = checked;
      $[25] = filterSetter;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    const t11 = checked && "bg-accent";
    let t12;
    if ($[27] !== t11) {
      t12 = cx("justify-between rounded-lg", t11);
      $[27] = t11;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== Icon) {
      t13 = Icon && <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" />;
      $[29] = Icon;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== label) {
      t14 = <span>{label}</span>;
      $[31] = label;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] !== t13 || $[34] !== t14) {
      t15 = <span className="flex items-center">{t13}{t14}</span>;
      $[33] = t13;
      $[34] = t14;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    const t16 = checked ? "on" : "off";
    let t17;
    if ($[36] !== t16) {
      t17 = <FilterSwitch state={t16} />;
      $[36] = t16;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    let t18;
    if ($[38] !== checked || $[39] !== t10 || $[40] !== t12 || $[41] !== t15 || $[42] !== t17) {
      t18 = <DropdownMenuItem role="menuitemcheckbox" aria-checked={checked} onSelect={t10} className={t12}>{t15}{t17}</DropdownMenuItem>;
      $[38] = checked;
      $[39] = t10;
      $[40] = t12;
      $[41] = t15;
      $[42] = t17;
      $[43] = t18;
    } else {
      t18 = $[43];
    }
    return t18;
  }
  if (isPlainYesNo(type, options)) {
    const current = singleBooleanValue(filterValue);
    const state = String(current) === "true" ? "on" : String(current) === "false" ? "off" : "unset";
    const isSet = state !== "unset";
    const canClear = isSet && !disableClear;
    const t10 = state === "on" ? true : state === "off" ? false : "mixed";
    const t11 = canClear ? "Backspace Delete" : undefined;
    let t12;
    if ($[44] !== filterSetter || $[45] !== state) {
      t12 = event_0 => {
        event_0.preventDefault();
        filterSetter(state === "on" ? "false" : "true");
      };
      $[44] = filterSetter;
      $[45] = state;
      $[46] = t12;
    } else {
      t12 = $[46];
    }
    let t13;
    if ($[47] !== canClear || $[48] !== filterSetter) {
      t13 = event_1 => {
        if (canClear && (event_1.key === "Backspace" || event_1.key === "Delete")) {
          event_1.preventDefault();
          filterSetter(undefined);
        }
      };
      $[47] = canClear;
      $[48] = filterSetter;
      $[49] = t13;
    } else {
      t13 = $[49];
    }
    const t14 = isSet && "bg-accent";
    let t15;
    if ($[50] !== t14) {
      t15 = cx("justify-between rounded-lg", t14);
      $[50] = t14;
      $[51] = t15;
    } else {
      t15 = $[51];
    }
    let t16;
    if ($[52] !== Icon || $[53] !== canClear || $[54] !== filterSetter) {
      t16 = canClear ? <span aria-hidden="true" onClick={event_2 => {
        event_2.preventDefault();
        event_2.stopPropagation();
        filterSetter(undefined);
      }} className="relative z-20 mr-1 inline-flex cursor-pointer items-center justify-center rounded hover:bg-hover"><IoIosClose className="h-4 w-4" /></span> : Icon ? <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" /> : null;
      $[52] = Icon;
      $[53] = canClear;
      $[54] = filterSetter;
      $[55] = t16;
    } else {
      t16 = $[55];
    }
    let t17;
    if ($[56] !== label) {
      t17 = <span>{label}</span>;
      $[56] = label;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    let t18;
    if ($[58] !== t16 || $[59] !== t17) {
      t18 = <span className="flex items-center">{t16}{t17}</span>;
      $[58] = t16;
      $[59] = t17;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    let t19;
    if ($[61] !== state) {
      t19 = <FilterSwitch state={state} />;
      $[61] = state;
      $[62] = t19;
    } else {
      t19 = $[62];
    }
    let t20;
    if ($[63] !== t10 || $[64] !== t11 || $[65] !== t12 || $[66] !== t13 || $[67] !== t15 || $[68] !== t18 || $[69] !== t19) {
      t20 = <DropdownMenuItem role="menuitemcheckbox" aria-checked={t10} aria-keyshortcuts={t11} onSelect={t12} onKeyDown={t13} className={t15}>{t18}{t19}</DropdownMenuItem>;
      $[63] = t10;
      $[64] = t11;
      $[65] = t12;
      $[66] = t13;
      $[67] = t15;
      $[68] = t18;
      $[69] = t19;
      $[70] = t20;
    } else {
      t20 = $[70];
    }
    return t20;
  }
  if (options?.length === 0) {
    return null;
  }
  let t10;
  if ($[71] !== clock || $[72] !== filterValue || $[73] !== options || $[74] !== type) {
    t10 = formatFilterDisplayValue(filterValue, type, options, clock);
    $[71] = clock;
    $[72] = filterValue;
    $[73] = options;
    $[74] = type;
    $[75] = t10;
  } else {
    t10 = $[75];
  }
  const {
    fullDisplayValue,
    truncatedDisplayValue
  } = t10;
  let t11;
  if ($[76] !== Icon || $[77] !== disableClear || $[78] !== filterValue || $[79] !== handleClearFilter) {
    t11 = !isEmptyValue(filterValue) && !disableClear ? <span role="button" aria-label="Clear filter" tabIndex={-1} onMouseDown={handleClearFilter} className="relative z-20 mr-1 inline-flex cursor-pointer items-center justify-center rounded hover:bg-hover"><IoIosClose className="h-4 w-4" /></span> : Icon ? <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" /> : null;
    $[76] = Icon;
    $[77] = disableClear;
    $[78] = filterValue;
    $[79] = handleClearFilter;
    $[80] = t11;
  } else {
    t11 = $[80];
  }
  let t12;
  if ($[81] !== filterValue || $[82] !== fullDisplayValue || $[83] !== truncatedDisplayValue) {
    t12 = !isEmptyValue(filterValue) && truncatedDisplayValue && <><div className="mx-2 h-3 self-center border-l" /><span className="text-caption-emphasis text-primary"><Tooltip content={fullDisplayValue}>{truncatedDisplayValue}</Tooltip></span></>;
    $[81] = filterValue;
    $[82] = fullDisplayValue;
    $[83] = truncatedDisplayValue;
    $[84] = t12;
  } else {
    t12 = $[84];
  }
  let t13;
  if ($[85] !== label || $[86] !== t11 || $[87] !== t12) {
    t13 = <>{t11}{label}{t12}</>;
    $[85] = label;
    $[86] = t11;
    $[87] = t12;
    $[88] = t13;
  } else {
    t13 = $[88];
  }
  const triggerContent = t13;
  let t14;
  if ($[89] !== filterValue) {
    t14 = !isEmptyValue(filterValue) && "bg-accent";
    $[89] = filterValue;
    $[90] = t14;
  } else {
    t14 = $[90];
  }
  let t15;
  if ($[91] !== t14) {
    t15 = cx("rounded-lg", t14);
    $[91] = t14;
    $[92] = t15;
  } else {
    t15 = $[92];
  }
  let t16;
  if ($[93] !== openSide || $[94] !== t15 || $[95] !== triggerContent) {
    t16 = <DropdownMenuSubMenuTrigger className={t15} arrowDirection={openSide}>{triggerContent}</DropdownMenuSubMenuTrigger>;
    $[93] = openSide;
    $[94] = t15;
    $[95] = triggerContent;
    $[96] = t16;
  } else {
    t16 = $[96];
  }
  let t17;
  if ($[97] !== type) {
    t17 = cx("min-w-0 overflow-visible", filterContentWidthClass(type));
    $[97] = type;
    $[98] = t17;
  } else {
    t17 = $[98];
  }
  const t18 = isMobile ? -40 : -1;
  let t19;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="min-w-[min(200px,calc(var(--radix-popper-available-width)-12px))] px-1.5 py-1"><FilterInput /></div>;
    $[99] = t19;
  } else {
    t19 = $[99];
  }
  let t20;
  if ($[100] !== editContextValue) {
    t20 = <FilterEditContext value={editContextValue}>{t19}</FilterEditContext>;
    $[100] = editContextValue;
    $[101] = t20;
  } else {
    t20 = $[101];
  }
  let t21;
  if ($[102] !== handleContentAnimationEnd || $[103] !== t17 || $[104] !== t18 || $[105] !== t20) {
    t21 = <DropdownMenuSubMenuContent ref={trackOpenSide} className={t17} sideOffset={t18} onAnimationEnd={handleContentAnimationEnd}>{t20}</DropdownMenuSubMenuContent>;
    $[102] = handleContentAnimationEnd;
    $[103] = t17;
    $[104] = t18;
    $[105] = t20;
    $[106] = t21;
  } else {
    t21 = $[106];
  }
  let t22;
  if ($[107] !== isOpen || $[108] !== t16 || $[109] !== t21) {
    t22 = <DropdownMenuSubMenu open={isOpen} onOpenChange={setIsOpen}>{t16}{t21}</DropdownMenuSubMenu>;
    $[107] = isOpen;
    $[108] = t16;
    $[109] = t21;
    $[110] = t22;
  } else {
    t22 = $[110];
  }
  return t22;
};
export default DropdownTableFilter;